import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, Breadcrumbs, CircularProgress, Grid, Link, TextField, Typography } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { CrystalButtonPopup, Loading, MostActiveZones, ZonesTabInZones } from 'components';
import { ROUTES } from 'consts';
import { BuildingCreateDialog } from 'dialogs/building';
import { ZoneCreateDialog } from 'dialogs/zone';
import { useQueryValues } from 'hooks/url.hook';
import { useBuildings } from 'pages/buildings/buildings.hook';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { getBuildingLocation } from 'utils';

interface ZonesLayoutProps {
  children?: React.ReactNode;
  hideCreateButton?: boolean;
}

export const ZonesLayout = ({ children = null, hideCreateButton = false }: ZonesLayoutProps) => {
  const [loading, buildings] = useBuildings();
  const { building: buildingIdQuery } = useQueryValues(['building']);
  const { orgSlug, isAdmin } = useAppSelector(selectCurrentUserInfo);
  const navigate = useNavigate();
  const [visibleBuildingCreateDialog, setVisibleBuildingCreateDialog] = useState<boolean>(false);
  const [visibleZoneCreateDialog, setVisibleZoneCreateDialog] = useState<boolean>(false);

  const buildingId = buildingIdQuery ? Number(buildingIdQuery) : undefined;
  const building = buildingId ? buildings.find((item) => item.id === buildingId) : undefined;

  const pageMenuItems = [
    {
      id: 1,
      label: 'Building',
      onClick: () => setVisibleBuildingCreateDialog(true),
    },
    {
      id: 2,
      label: 'Zone',
      onClick: () => setVisibleZoneCreateDialog(true),
    },
  ];

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Breadcrumbs
                separator={
                  <Box sx={{ paddingX: 1.5 }}>
                    <ArrowForwardIos sx={{ fontSize: 10 }} />
                  </Box>
                }
              >
                <Link
                  component="button"
                  variant="subtitle2"
                  color="textSecondary"
                  onClick={() => {
                    navigate(`/${orgSlug}/${ROUTES.ZONES}`);
                  }}
                >
                  Zones
                </Link>

                {loading || (buildingId && !building) ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      marginTop: 0.5,
                    }}
                  />
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {building ? getBuildingLocation(building) : 'All'}
                  </Typography>
                )}
              </Breadcrumbs>

              {!hideCreateButton && (
                <Box sx={{ marginLeft: 'auto' }}>
                  {isAdmin && <CrystalButtonPopup items={pageMenuItems} label="" />}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={8} lg={6} xl={4}>
            <Autocomplete
              value={buildingId}
              options={buildings.map((item) => item.id)}
              getOptionLabel={(option) => {
                const building = buildings.find((item) => item.id === option);

                if (building) {
                  return getBuildingLocation(building);
                }

                return '';
              }}
              onChange={(_, newValue) => {
                if (newValue) {
                  navigate(`?building=${newValue}`);
                } else {
                  navigate('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Zones by Buildings"
                  placeholder="Enter search keyword..."
                  size="small"
                  sx={{ bgcolor: 'white' }}
                />
              )}
              fullWidth
              loading={loading}
              loadingText={<Loading text="Loading buildings..." height={120} />}
            />
          </Grid>

          {false && (
            <Grid item xs={12}>
              <MostActiveZones />
            </Grid>
          )}

          <Grid item xs={12}>
            <ZonesTabInZones />

            {children}
          </Grid>
        </Grid>
      </Box>

      <BuildingCreateDialog visible={visibleBuildingCreateDialog} setVisible={setVisibleBuildingCreateDialog} />

      <ZoneCreateDialog
        visible={visibleZoneCreateDialog}
        setVisible={setVisibleZoneCreateDialog}
        buildingId={buildingId}
      />
    </>
  );
};
